import React, { useState, useEffect } from 'react';
import { FileText, Upload, HardDrive, Files, RefreshCw, FolderUp } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import FileUploadModal from '@/components/admin/FileUploadModal';
import BulkFileUploadModal from '@/components/admin/BulkFileUploadModal';
import FileManagementTable from '@/components/admin/FileManagementTable';
import { adminFileService, CourseFile } from '@/services/adminFileService';

export default function AdminFiles() {
  const [files, setFiles] = useState<CourseFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [stats, setStats] = useState({
    totalFiles: 0,
    totalSizeMb: 0,
    averageSizeKb: 0,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminFileService.listFiles();
      setFiles(response.files);
      setStats({
        totalFiles: response.total_count,
        totalSizeMb: response.total_size_bytes / (1024 * 1024),
        averageSizeKb: response.total_count > 0
          ? (response.total_size_bytes / 1024) / response.total_count
          : 0,
      });
    } catch (error: any) {
      console.error('Error fetching files:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to fetch files');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (fileId: string) => {
    try {
      await adminFileService.deleteFile(fileId);
      await fetchFiles();
    } catch (error: any) {
      console.error('Error deleting file:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to delete file');
      throw error;
    }
  };

  const handleUploadSuccess = () => {
    fetchFiles();
  };

  const formatStorageSize = (sizeMb: number) => {
    if (sizeMb < 1) {
      return `${(sizeMb * 1024).toFixed(2)} KB`;
    }
    if (sizeMb >= 1024) {
      return `${(sizeMb / 1024).toFixed(2)} GB`;
    }
    return `${sizeMb.toFixed(2)} MB`;
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-indigo-900/20 to-purple-900/20 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Course Files Management</h1>
              <p className="text-gray-400">
                Upload and manage course resources, templates, and supplementary materials
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={fetchFiles}
                disabled={isLoading}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg transition-all flex items-center gap-2 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={() => setShowBulkUploadModal(true)}
                className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg transition-all flex items-center gap-2"
              >
                <FolderUp className="w-4 h-4" />
                Bulk Upload
              </button>
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-all flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload File
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Total Files */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl blur-xl opacity-20 group-hover:opacity-30 transition-all"></div>
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/20 hover:border-white/30 transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-indigo-600/20 rounded-lg">
                    <Files className="w-6 h-6 text-indigo-400" />
                  </div>
                  <span className="text-2xl font-bold text-white">
                    {stats.totalFiles}
                  </span>
                </div>
                <p className="text-gray-400 text-sm">Total Files</p>
              </div>
            </div>

            {/* Total Storage */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl blur-xl opacity-20 group-hover:opacity-30 transition-all"></div>
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/20 hover:border-white/30 transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-purple-600/20 rounded-lg">
                    <HardDrive className="w-6 h-6 text-purple-400" />
                  </div>
                  <span className="text-2xl font-bold text-white">
                    {formatStorageSize(stats.totalSizeMb)}
                  </span>
                </div>
                <p className="text-gray-400 text-sm">Total Storage Used</p>
              </div>
            </div>

            {/* Average Size */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-red-600 rounded-xl blur-xl opacity-20 group-hover:opacity-30 transition-all"></div>
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/20 hover:border-white/30 transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-pink-600/20 rounded-lg">
                    <FileText className="w-6 h-6 text-pink-400" />
                  </div>
                  <span className="text-2xl font-bold text-white">
                    {stats.averageSizeKb.toFixed(1)} KB
                  </span>
                </div>
                <p className="text-gray-400 text-sm">Average File Size</p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {/* File Table */}
          <div className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 backdrop-blur-xl rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Uploaded Files</h2>
              <p className="text-gray-400 text-sm">
                {files.length} {files.length === 1 ? 'file' : 'files'} found
              </p>
            </div>

            <FileManagementTable
              files={files}
              isLoading={isLoading}
              onDelete={handleDelete}
              onRefresh={fetchFiles}
            />
          </div>

          {/* Storage Information */}
          <div className="mt-8 p-4 bg-gradient-to-br from-white/5 to-white/0 rounded-lg border border-white/10">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-600/20 rounded-lg">
                <HardDrive className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-blue-400 mb-1">
                  Storage Information
                </h3>
                <p className="text-xs text-gray-400">
                  Files are stored in AWS S3. Current usage: {formatStorageSize(stats.totalSizeMb)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Maximum file size: 1GB | Supported: PDF, DOC, XLS, PPT, Images, Archives, Code files
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      <FileUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={handleUploadSuccess}
      />

      {/* Bulk Upload Modal */}
      <BulkFileUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onUploadSuccess={handleUploadSuccess}
      />
    </AdminLayout>
  );
}
